"use client";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth-context";
import { apiJson } from "./api";
type Row = Record<string, any>;
type Data = {
  members: Row[];
  payments: Row[];
  contacts: Row[];
  partners: Row[];
  uses: Row[];
};
const empty: Data = {
  members: [],
  payments: [],
  contacts: [],
  partners: [],
  uses: [],
};
const nav = [
  ["dashboard", "⌂", "Dashboard"],
  ["members", "♙", "Membros"],
  ["payments", "▣", "Pagamentos"],
  ["late", "!", "Inadimplentes"],
  ["partners", "♧", "Parceiros"],
  ["uses", "↗", "Utilizações"],
  ["reports", "▥", "Relatórios"],
  ["contacts", "✉", "Comunicação"],
  ["users", "⚿", "Usuários e permissões"],
];
const money = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    v || 0,
  );
const br = (d: string) => (d ? d.split("-").reverse().join("/") : "—");
function days(d: string) {
  return Math.ceil(
    (new Date(d + "T12:00:00").getTime() -
      new Date("2026-07-20T12:00:00").getTime()) /
      86400000,
  );
}
function situation(m: Row) {
  if (m.status === "Cancelado") return ["Cancelado", "cancel"];
  const n = days(m.due_date);
  return n < 0
    ? ["Vencido", "red"]
    : n === 0
      ? ["Vence hoje", "orange"]
      : n <= 10
        ? ["A vencer", "yellow"]
        : ["Em dia", "green"];
}
export default function Home() {
  const { user, logout, can } = useAuth();
  const [view, setView] = useState(can("dashboard") ? "dashboard" : "uses"),
    [data, setData] = useState<Data>(empty),
    [loading, setLoading] = useState(true),
    [modal, setModal] = useState<string | null>(null),
    [selected, setSelected] = useState<Row | null>(null),
    [toast, setToast] = useState(""),
    [mobile, setMobile] = useState(false);
  async function load() {
    setLoading(true);
    const r = await fetch("/api/data.php");
    const j = await apiJson(r);
    if (r.ok) setData(j);
    else alert(j.error);
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, []);
  async function act(body: Row) {
    const r = await fetch("/api/data.php", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = await apiJson(r);
    if (!r.ok) throw new Error(j.error);
    setData(j);
  }
  function done(s: string) {
    setModal(null);
    setSelected(null);
    setToast(s);
    setTimeout(() => setToast(""), 2500);
  }
  function open(type: string, row?: Row) {
    setSelected(row || null);
    setModal(type);
  }
  const initials = user.name
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("");
  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <span className="brandmark">●●●</span>
          <div>
            <strong>
              ASSOCIA<span>PRO</span>
            </strong>
            <small>GESTÃO INTELIGENTE</small>
          </div>
        </div>
        <nav>
          {nav
            .filter((n) => can(n[0]))
            .map((n) => (
              <button
                key={n[0]}
                className={view === n[0] ? "active" : ""}
                onClick={() => {
                  setView(n[0]);
                  setMobile(false);
                }}
              >
                <i>{n[1]}</i>
                {n[2]}
              </button>
            ))}
        </nav>
        <div className="profile">
          <div className="avatar photo">{initials}</div>
          <div>
            <b>{user.name}</b>
            <small>{user.role}</small>
          </div>
          <button className="logout-mini" onClick={logout} title="Sair">
            ↪
          </button>
        </div>
      </aside>
      <main className="workspace">
        <header>
          <button className="hamb" onClick={() => setMobile(!mobile)}>
            ☰
          </button>
          <div className="mobile-brand">
            ASSOCIA<span>PRO</span>
          </div>
          <div className="top-actions">
            <div className="avatar photo">{initials}</div>
            <div className="admin">
              <b>{user.name}</b>
              <small>{user.role}</small>
            </div>
            <button className="ghost top-logout" onClick={logout}>
              Sair
            </button>
          </div>
        </header>
        {loading ? (
          <div className="loading">
            <div />
            <p>Carregando dados da associação...</p>
          </div>
        ) : (
          <>
            {view === "dashboard" && <Dashboard d={data} go={setView} />}{" "}
            {view === "members" && (
              <Members d={data} open={open} act={act} done={done} />
            )}{" "}
            {view === "payments" && <Payments d={data} open={open} />}{" "}
            {view === "late" && <Late d={data} open={open} />}{" "}
            {view === "partners" && <Partners d={data} open={open} />}{" "}
            {view === "uses" && <Uses d={data} open={open} />}{" "}
            {view === "reports" && <Reports d={data} />}{" "}
            {view === "contacts" && <Contacts d={data} open={open} />}{" "}
            {view === "users" && (
              <UserManagement
                current={user}
                partners={data.partners}
                members={data.members}
              />
            )}
          </>
        )}
      </main>
      {modal === "member" && (
        <MemberModal
          row={selected}
          close={() => setModal(null)}
          save={async (x) => {
            await act({ action: "member.save", member: x });
            done(selected ? "Cadastro atualizado" : "Membro cadastrado");
          }}
        />
      )}
      {modal === "payment" && (
        <PaymentModal
          members={data.members}
          selected={selected}
          close={() => setModal(null)}
          save={async (x) => {
            await act({ action: "payment.add", ...x });
            done("Pagamento registrado e vencimento renovado");
          }}
        />
      )}
      {modal === "contact" && (
        <ContactModal
          members={data.members}
          selected={selected}
          close={() => setModal(null)}
          save={async (x) => {
            await act({ action: "contact.add", ...x });
            done("Contato registrado no histórico");
          }}
        />
      )}
      {modal === "partner" && (
        <PartnerModal
          row={selected}
          close={() => setModal(null)}
          save={async (x) => {
            await act({ action: "partner.save", ...x });
            done(selected ? "Parceiro atualizado" : "Parceiro cadastrado");
          }}
        />
      )}
      {modal === "use" && (
        <UseModal
          d={data}
          close={() => setModal(null)}
          save={async (x) => {
            await act({ action: "use.add", ...x });
            done("Utilização registrada");
          }}
        />
      )}
      {toast && <div className="toast">✓ {toast}</div>}
      {mobile && <div className="shade" onClick={() => setMobile(false)} />}
    </div>
  );
}
function Title({
  title,
  sub,
  children,
}: {
  title: string;
  sub: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        <h1>{title}</h1>
        <p>{sub}</p>
      </div>
      {children}
    </div>
  );
}
function Dashboard({ d, go }: { d: Data; go: (v: string) => void }) {
  const active = d.members.filter((m) => m.status === "Ativo").length,
    late = d.members.filter((m) => days(m.due_date) < 0).length,
    month = d.payments.filter((p) => p.paid_at.startsWith("2026-07")),
    revenue = month.reduce((a, p) => a + p.amount, 0),
    upcoming = d.members
      .filter((m) => days(m.due_date) >= 0 && days(m.due_date) <= 7)
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    overdue = d.members
      .filter((m) => days(m.due_date) < 0)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  return (
    <div className="page">
      <Title
        title="Olá, Gleiton! 👋"
        sub="Bem-vindo ao painel de gestão da associação."
      />
      <div className="metrics dash4">
        <Metric
          icon="♙"
          color="blue"
          label="Membros ativos"
          value={String(active)}
          sub={`${d.members.length} cadastrados`}
        />
        <Metric
          icon="!"
          color="amber"
          label="Inadimplentes"
          value={String(late)}
          sub="Precisam de contato"
        />
        <Metric
          icon="$"
          color="green"
          label="Receita do mês"
          value={money(revenue)}
          sub={`${month.length} pagamentos`}
        />
        <Metric
          icon="↗"
          color="purple"
          label="Receita do ano"
          value={money(d.payments.reduce((a, p) => a + p.amount, 0))}
          sub="2026"
        />
      </div>
      <div className="dash-grid">
        <ListCard
          title="Próximos vencimentos (7 dias)"
          click={() => go("members")}
          rows={upcoming}
          kind="member"
        />
        <ListCard
          title="Inadimplentes do mês"
          click={() => go("late")}
          rows={overdue}
          kind="late"
        />
        <ListCard
          title="Parceiros ativos"
          click={() => go("partners")}
          rows={d.partners}
          kind="partner"
        />
      </div>
      <section className="panel recent">
        <div className="panel-head">
          <h3>Últimos pagamentos registrados</h3>
          <button className="link" onClick={() => go("payments")}>
            Ver todos
          </button>
        </div>
        <PaymentTable rows={d.payments.slice(0, 6)} />
      </section>
    </div>
  );
}
function Metric({
  icon,
  color,
  label,
  value,
  sub,
}: {
  icon: string;
  color: string;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="metric">
      <div className={`metric-icon ${color}`}>{icon}</div>
      <div>
        <p>{label}</p>
        <h2>{value}</h2>
        <small>{sub}</small>
      </div>
    </div>
  );
}
function ListCard({
  title,
  click,
  rows,
  kind,
}: {
  title: string;
  click: () => void;
  rows: Row[];
  kind: string;
}) {
  return (
    <section className="panel card">
      <div className="panel-head">
        <h3>{title}</h3>
        <button className="link" onClick={click}>
          Ver todos
        </button>
      </div>
      {rows.slice(0, 5).map((r, i) => (
        <div className="person" key={r.id}>
          <Avatar row={r} />
          <div>
            <b>{r.name}</b>
            <small className={kind === "late" ? "red" : ""}>
              {kind === "partner"
                ? r.category
                : kind === "late"
                  ? `Vencido há ${Math.abs(days(r.due_date))} dias`
                  : `Vence ${days(r.due_date) === 0 ? "hoje" : `em ${days(r.due_date)} dias`}`}
            </small>
          </div>
          <time>{kind === "partner" ? r.benefit : br(r.due_date)}</time>
        </div>
      ))}
      {!rows.length && <p className="empty">Nenhum registro.</p>}
    </section>
  );
}
function Avatar({ row }: { row: Row }) {
  const image = row.photo || row.logo;
  return image ? (
    <img className="mini-avatar avatar-img" src={image} alt="" />
  ) : (
    <div className="mini-avatar">
      {row.name
        .split(" ")
        .map((x: string) => x[0])
        .slice(0, 2)}
    </div>
  );
}
function Members({
  d,
  open,
  act,
  done,
}: {
  d: Data;
  open: (x: string, r?: Row) => void;
  act: (b: Row) => Promise<void>;
  done: (s: string) => void;
}) {
  const [q, setQ] = useState(""),
    [filter, setFilter] = useState("Todos");
  const list = d.members.filter(
    (m) =>
      (m.name + " " + m.cpf + " " + m.email)
        .toLowerCase()
        .includes(q.toLowerCase()) &&
      (filter === "Todos" || situation(m)[0] === filter),
  );
  return (
    <div className="page">
      <Title
        title="Lista de membros"
        sub={`${d.members.length} associados cadastrados`}
      >
        <button className="primary" onClick={() => open("member")}>
          ＋ Novo membro
        </button>
      </Title>
      <section className="panel">
        <div className="toolbar">
          <label className="search">
            ⌕
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome, CPF ou e-mail..."
            />
          </label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option>Todos</option>
            <option>Em dia</option>
            <option>A vencer</option>
            <option>Vence hoje</option>
            <option>Vencido</option>
          </select>
          <button
            className="ghost"
            onClick={() => {
              setQ("");
              setFilter("Todos");
            }}
          >
            Limpar filtros
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Membro</th>
                <th>Contato</th>
                <th>Status</th>
                <th>Próximo vencimento</th>
                <th>Situação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                const s = situation(m);
                return (
                  <tr key={m.id}>
                    <td>
                      <div className="member-cell">
                        <Avatar row={m} />
                        <div>
                          <b>{m.name}</b>
                          <small>Carteirinha {m.card_number}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {m.phone}
                      <small className="block">{m.email}</small>
                    </td>
                    <td>{m.status}</td>
                    <td>{br(m.due_date)}</td>
                    <td>
                      <span className={`status ${s[1]}`}>{s[0]}</span>
                    </td>
                    <td>
                      <button
                        className="rowbtn"
                        onClick={() => open("member", m)}
                      >
                        ✎ Editar
                      </button>
                      <button
                        className="rowbtn pay"
                        onClick={() => open("payment", m)}
                      >
                        ＋ Pagamento
                      </button>
                      <button
                        className="rowbtn danger"
                        onClick={async () => {
                          if (confirm(`Excluir ${m.name}?`)) {
                            await act({ action: "member.delete", id: m.id });
                            done("Membro excluído");
                          }
                        }}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
function Payments({
  d,
  open,
}: {
  d: Data;
  open: (x: string, r?: Row) => void;
}) {
  return (
    <div className="page">
      <Title
        title="Pagamentos"
        sub="Histórico financeiro e renovações automáticas."
      >
        <button className="primary" onClick={() => open("payment")}>
          ＋ Novo pagamento
        </button>
      </Title>
      <div className="metrics compact">
        <Metric
          icon="$"
          color="green"
          label="Receita total"
          value={money(d.payments.reduce((a, p) => a + p.amount, 0))}
          sub={`${d.payments.length} pagamentos`}
        />
        <Metric
          icon="▣"
          color="blue"
          label="Ticket médio"
          value={money(
            d.payments.reduce((a, p) => a + p.amount, 0) /
              (d.payments.length || 1),
          )}
          sub="Valor por pagamento"
        />
        <Metric
          icon="!"
          color="amber"
          label="Atrasados"
          value={String(d.members.filter((m) => days(m.due_date) < 0).length)}
          sub="Associados"
        />
      </div>
      <section className="panel recent">
        <PaymentTable rows={d.payments} />
      </section>
    </div>
  );
}
function PaymentTable({ rows }: { rows: Row[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Membro</th>
            <th>Valor</th>
            <th>Pagamento</th>
            <th>Próximo vencimento</th>
            <th>Plano</th>
            <th>Forma</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td>
                <div className="member-cell">
                  <Avatar
                    row={{ name: p.member_name, photo: p.member_photo }}
                  />
                  <b>{p.member_name}</b>
                </div>
              </td>
              <td>{money(p.amount)}</td>
              <td>{br(p.paid_at)}</td>
              <td>{br(p.next_due)}</td>
              <td>{p.frequency}</td>
              <td>{p.method}</td>
              <td>
                <span className="status green">Pago</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Late({ d, open }: { d: Data; open: (x: string, r?: Row) => void }) {
  const list = d.members.filter((m) => days(m.due_date) < 0);
  return (
    <div className="page">
      <Title
        title="Inadimplentes do mês"
        sub="Controle de cobranças e contatos."
      />
      <div className="alert">! {list.length} membros inadimplentes</div>
      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Membro</th>
                <th>Último vencimento</th>
                <th>Dias em atraso</th>
                <th>Contato</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="member-cell">
                      <Avatar row={m} />
                      <b>{m.name}</b>
                    </div>
                  </td>
                  <td>{br(m.due_date)}</td>
                  <td className="danger-text">
                    {Math.abs(days(m.due_date))} dias
                  </td>
                  <td>{m.phone}</td>
                  <td>
                    <button
                      className="rowbtn pay"
                      onClick={() => open("contact", m)}
                    >
                      ✉ Registrar contato
                    </button>
                    <button
                      className="rowbtn"
                      onClick={() => open("payment", m)}
                    >
                      ＋ Pagamento
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
function Partners({
  d,
  open,
}: {
  d: Data;
  open: (x: string, row?: Row) => void;
}) {
  return (
    <div className="page">
      <Title title="Parceiros" sub="Rede de benefícios da associação.">
        <button className="primary" onClick={() => open("partner")}>
          ＋ Novo parceiro
        </button>
      </Title>
      <div className="partner-grid">
        {d.partners.map((p) => (
          <article className="panel partner-card" key={p.id}>
            {p.logo ? (
              <img className="partner-card-logo" src={p.logo} alt={p.name} />
            ) : (
              <div className="partner-logo large">
                {p.name
                  .split(" ")
                  .map((x: string) => x[0])
                  .slice(0, 2)}
              </div>
            )}
            <span className="status green">{p.status}</span>
            <h3>{p.name}</h3>
            <p>{p.category}</p>
            <div>
              <b>{p.benefit}</b>
            </div>
            <footer>
              <span>{p.address}</span>
              <button onClick={() => open("partner", p)}>✎ Editar</button>
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
}
function Uses({ d, open }: { d: Data; open: (x: string) => void }) {
  return (
    <div className="page">
      <Title
        title="Utilizações de benefícios"
        sub="Registro real de uso pelos associados."
      >
        <button className="primary" onClick={() => open("use")}>
          ＋ Registrar utilização
        </button>
      </Title>
      <section className="panel recent">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Associado</th>
                <th>Parceiro</th>
                <th>Data</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {d.uses.map((u) => (
                <tr key={u.id}>
                  <td>
                    <b>{u.member_name}</b>
                  </td>
                  <td>{u.partner_name}</td>
                  <td>{br(u.used_at)}</td>
                  <td>{u.detail || "Benefício validado"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.uses.length && (
            <p className="empty">
              Nenhuma utilização registrada. Clique em “Registrar utilização”
              para testar.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
function Contacts({ d, open }: { d: Data; open: (x: string) => void }) {
  return (
    <div className="page">
      <Title
        title="Comunicação com membros"
        sub="Tudo fica registrado no histórico."
      >
        <button className="primary" onClick={() => open("contact")}>
          ＋ Registrar contato
        </button>
      </Title>
      <section className="panel recent">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Membro</th>
                <th>Data</th>
                <th>Canal</th>
                <th>Responsável</th>
                <th>Respondeu?</th>
                <th>Próximo contato</th>
                <th>Observações</th>
              </tr>
            </thead>
            <tbody>
              {d.contacts.map((c) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.member_name}</b>
                  </td>
                  <td>{br(c.contacted_at)}</td>
                  <td>{c.type}</td>
                  <td>{c.responsible}</td>
                  <td>
                    <span
                      className={`status ${c.answered ? "green" : "yellow"}`}
                    >
                      {c.answered ? "Sim" : "Não"}
                    </span>
                  </td>
                  <td>{br(c.next_contact)}</td>
                  <td>{c.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.contacts.length && (
            <p className="empty">Nenhum contato registrado ainda.</p>
          )}
        </div>
      </section>
    </div>
  );
}
function Reports({ d }: { d: Data }) {
  const rev = d.payments.reduce((a, p) => a + p.amount, 0),
    late = d.members.filter((m) => days(m.due_date) < 0).length;
  return (
    <div className="page">
      <Title
        title="Relatórios"
        sub="Indicadores calculados com os dados cadastrados."
      />
      <div className="report-cards">
        {[
          ["Receita registrada", money(rev), `${d.payments.length} pagamentos`],
          [
            "Associados",
            String(d.members.length),
            `${d.members.filter((m) => m.status === "Ativo").length} ativos`,
          ],
          [
            "Inadimplência",
            `${Math.round((late / (d.members.length || 1)) * 100)}%`,
            `${late} membros`,
          ],
          ["Parceiros", String(d.partners.length), "ativos na rede"],
          ["Utilizações", String(d.uses.length), "benefícios validados"],
        ].map((x) => (
          <div className="panel" key={x[0]}>
            <small>{x[0]}</small>
            <h2>{x[1]}</h2>
            <p>{x[2]}</p>
          </div>
        ))}
      </div>
      <section className="panel chart">
        <div className="panel-head">
          <h3>Receita por pagamento</h3>
          <b>{money(rev)}</b>
        </div>
        <div className="bars">
          {d.payments
            .slice(0, 8)
            .reverse()
            .map((p, i) => (
              <div key={p.id}>
                <span
                  style={{
                    height: `${Math.max(25, Math.min(100, p.amount / 2))}%`,
                  }}
                />
                <small>{br(p.paid_at).slice(0, 5)}</small>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
function UserManagement({
  current,
  partners,
  members,
}: {
  current: Row;
  partners: Row[];
  members: Row[];
}) {
  const [users, setUsers] = useState<Row[]>([]),
    [logs, setLogs] = useState<Row[]>([]),
    [editing, setEditing] = useState<Row | null>(null),
    [loading, setLoading] = useState(true),
    [message, setMessage] = useState("");
  async function reload() {
    const r = await fetch("/api/auth.php");
    const j = await apiJson(r);
    if (r.ok) {
      setUsers(j.users || []);
      setLogs(j.logs || []);
    }
    setLoading(false);
  }
  useEffect(() => {
    reload();
  }, []);
  async function action(body: Row) {
    const r = await fetch("/api/auth.php", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = await apiJson(r);
    if (!r.ok) throw new Error(j.error || "Não foi possível salvar o usuário.");
    setUsers(j.users);
    setLogs(j.logs);
    setEditing(null);
    setMessage("Alteração salva com segurança");
    setTimeout(() => setMessage(""), 2200);
  }
  return (
    <div className="page">
      <Title
        title="Usuários e permissões"
        sub="Controle de acesso real por login, senha e perfil."
      >
        <button
          className="primary"
          onClick={() =>
            setEditing({
              name: "",
              username: "",
              email: "",
              role: "Secretaria",
              active: true,
              password: "",
            })
          }
        >
          ＋ Novo usuário
        </button>
      </Title>
      <div className="role-summary">
        {[
          ["Administrador", "Acesso completo", "#1367e8"],
          ["Financeiro", "Pagamentos e relatórios", "#12a265"],
          ["Secretaria", "Membros e comunicação", "#8a4fd0"],
          ["Parceiro", "Validação de benefícios", "#e89416"],
          ["Membro", "Carteirinha e clube de descontos", "#07945b"],
        ].map((r) => (
          <div className="panel" key={r[0]}>
            <span style={{ background: r[2] }}>♙</span>
            <div>
              <b>{r[0]}</b>
              <small>{r[1]}</small>
            </div>
            <strong>{users.filter((u) => u.role === r[0]).length}</strong>
          </div>
        ))}
      </div>
      <section className="panel user-panel">
        <div className="panel-head">
          <div>
            <h3>Contas de acesso</h3>
            <p>Senhas são protegidas e nunca são exibidas.</p>
          </div>
          {message && <span className="save-msg">✓ {message}</span>}
        </div>
        {loading ? (
          <p className="empty">Carregando usuários...</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Login</th>
                  <th>Perfil</th>
                  <th>Último acesso</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="member-cell">
                        <div className="mini-avatar">
                          {u.name
                            .split(" ")
                            .map((x: string) => x[0])
                            .slice(0, 2)}
                        </div>
                        <div>
                          <b>{u.name}</b>
                          <small>{u.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <code>{u.username}</code>
                    </td>
                    <td>
                      <span className="role-pill">{u.role}</span>
                    </td>
                    <td>
                      {u.last_login
                        ? new Date(u.last_login).toLocaleString("pt-BR")
                        : "Nunca acessou"}
                    </td>
                    <td>
                      <span className={`status ${u.active ? "green" : "red"}`}>
                        {u.active ? "Ativo" : "Bloqueado"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="rowbtn"
                        onClick={() => setEditing({ ...u, password: "" })}
                      >
                        ✎ Editar
                      </button>
                      {u.id !== current.id && (
                        <button
                          className="rowbtn danger"
                          onClick={() => {
                            if (confirm(`Excluir o acesso de ${u.name}?`))
                              action({ action: "user.delete", id: u.id }).catch(
                                (error) => alert(error.message),
                              );
                          }}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel audit">
        <div className="panel-head">
          <div>
            <h3>Registro de auditoria</h3>
            <p>Últimas ações realizadas no sistema.</p>
          </div>
        </div>
        {logs.slice(0, 10).map((l) => (
          <div className="audit-row" key={l.id}>
            <span>✓</span>
            <div>
              <b>{l.action}</b>
              <small>
                {l.user_name} • {l.detail || "Sistema"}
              </small>
            </div>
            <time>{new Date(l.created_at).toLocaleString("pt-BR")}</time>
          </div>
        ))}
      </section>
      {editing && (
        <UserModal
          row={editing}
          partners={partners}
          members={members}
          close={() => setEditing(null)}
          save={(x) => action({ action: "user.save", user: x })}
        />
      )}
    </div>
  );
}
function UserModal({
  row,
  partners,
  members,
  close,
  save,
}: {
  row: Row;
  partners: Row[];
  members: Row[];
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>(row);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal
      title={row.id ? "Editar usuário" : "Criar novo usuário"}
      close={close}
      save={async () => {
        if (busy) return;
        const clean = {
          ...f,
          name: String(f.name || "").trim(),
          username: String(f.username || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/[^a-z0-9._@-]/g, ""),
          email: String(f.email || "").trim(),
          partner_id: f.role === "Parceiro" ? Number(f.partner_id || 0) : null,
          member_id: f.role === "Membro" ? Number(f.member_id || 0) : null,
        };
        if (
          !clean.name ||
          !clean.username ||
          (clean.role === "Parceiro" && !clean.partner_id) ||
          (clean.role === "Membro" && !clean.member_id) ||
          (!clean.id && (!clean.password || clean.password.length < 8))
        )
          return setError(
            "Informe nome, usuário, o cadastro vinculado e uma senha de pelo menos 8 caracteres.",
          );
        setBusy(true);
        setError("");
        try {
          await save(clean);
        } catch (reason) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Não foi possível salvar o usuário.",
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="form-grid">
        <F
          label="Nome completo *"
          value={f.name}
          set={(x) => setF({ ...f, name: x })}
        />
        <F
          label="Usuário de acesso *"
          value={f.username}
          set={(x) =>
            setF({ ...f, username: x.toLowerCase().replace(/\s/g, "") })
          }
        />
        <F
          label="E-mail"
          type="email"
          value={f.email}
          set={(x) => setF({ ...f, email: x })}
        />
        <Select
          label="Perfil de acesso"
          value={f.role}
          set={(x) => setF({ ...f, role: x })}
          options={[
            "Administrador",
            "Financeiro",
            "Secretaria",
            "Parceiro",
            "Membro",
          ]}
        />
        {f.role === "Parceiro" && (
          <Select
            label="Empresa vinculada *"
            value={String(f.partner_id || "")}
            set={(x) => setF({ ...f, partner_id: Number(x) })}
            options={[
              { v: "", l: "Selecione o parceiro" },
              ...partners.map((partner) => ({
                v: String(partner.id),
                l: partner.name,
              })),
            ]}
          />
        )}
        {f.role === "Membro" && (
          <Select
            label="Membro vinculado *"
            value={String(f.member_id || "")}
            set={(x) => setF({ ...f, member_id: Number(x) })}
            options={[
              { v: "", l: "Selecione o membro" },
              ...members.map((member) => ({
                v: String(member.id),
                l: `${member.name} — ${member.card_number}`,
              })),
            ]}
          />
        )}
        <F
          label={
            row.id ? "Nova senha (deixe vazio para manter)" : "Senha inicial *"
          }
          type="password"
          value={f.password}
          set={(x) => setF({ ...f, password: x })}
        />
        {error && <div className="form-error wide">{error}</div>}
        <label className="check">
          <input
            type="checkbox"
            checked={Boolean(f.active)}
            onChange={(e) => setF({ ...f, active: e.target.checked })}
          />{" "}
          Usuário ativo
        </label>
      </div>
      <div className="permission-preview">
        <b>Permissões deste perfil</b>
        <p>
          {f.role === "Administrador"
            ? "Todas as áreas, configurações e usuários."
            : f.role === "Financeiro"
              ? "Dashboard, pagamentos, inadimplência, relatórios e comunicação."
              : f.role === "Secretaria"
                ? "Dashboard, membros, parceiros, utilizações e comunicação."
                : f.role === "Parceiro"
                  ? "Somente registro e consulta de utilizações."
                  : "Carteirinha digital, benefícios, parceiros, mensalidades e perfil próprio."}
        </p>
      </div>
    </Modal>
  );
}
function Modal({
  title,
  close,
  children,
  save,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
  save: () => void;
}) {
  return (
    <div
      className="modal-bg"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) close();
      }}
    >
      <div className="modal">
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            <p>Preencha os dados e clique em salvar.</p>
          </div>
          <button onClick={close}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">
          <button className="ghost" onClick={close}>
            Cancelar
          </button>
          <button className="primary" onClick={save}>
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
function MemberModal({
  row,
  close,
  save,
}: {
  row: Row | null;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>(
      row || {
        name: "",
        cpf: "",
        rg: "",
        birth_date: "",
        phone: "",
        whatsapp: "",
        email: "",
        address: "",
        district: "",
        city: "Belo Horizonte",
        state: "MG",
        zip: "",
        category: "Individual",
        joined_at: "2026-07-20",
        status: "Ativo",
        financial_responsible: "",
        notes: "",
        due_date: "2026-08-20",
        photo: "",
      },
    ),
    [busy, setBusy] = useState(false);
  function v(k: string, val: any) {
    setF({ ...f, [k]: val });
  }
  async function upload(file?: File) {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/upload.php", { method: "POST", body: fd });
    const j = await apiJson(r);
    if (r.ok) v("photo", j.url);
    else alert(j.error);
    setBusy(false);
  }
  return (
    <Modal
      title={row ? "Editar membro" : "Cadastro de membro"}
      close={close}
      save={async () => {
        if (!f.name || !f.cpf || !f.phone)
          return alert("Preencha nome, CPF e telefone.");
        setBusy(true);
        await save(f);
        setBusy(false);
      }}
    >
      <div className="edit-layout">
        <div className="photo-editor">
          {f.photo ? (
            <img src={f.photo} alt="Foto" />
          ) : (
            <div className="photo-placeholder">Foto</div>
          )}
          <label className="outline">
            {busy ? "Enviando..." : "Alterar foto"}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
        <div className="form-grid">
          <F label="Nome completo *" value={f.name} set={(x) => v("name", x)} />
          <F label="CPF *" value={f.cpf} set={(x) => v("cpf", x)} />
          <F label="RG" value={f.rg} set={(x) => v("rg", x)} />
          <F
            label="Nascimento"
            type="date"
            value={f.birth_date}
            set={(x) => v("birth_date", x)}
          />
          <F label="Telefone *" value={f.phone} set={(x) => v("phone", x)} />
          <F
            label="WhatsApp"
            value={f.whatsapp}
            set={(x) => v("whatsapp", x)}
          />
          <F
            label="E-mail"
            type="email"
            value={f.email}
            set={(x) => v("email", x)}
          />
          <F label="CEP" value={f.zip} set={(x) => v("zip", x)} />
          <F
            wide
            label="Endereço"
            value={f.address}
            set={(x) => v("address", x)}
          />
          <F label="Bairro" value={f.district} set={(x) => v("district", x)} />
          <F label="Cidade" value={f.city} set={(x) => v("city", x)} />
          <Select
            label="Categoria"
            value={f.category}
            set={(x) => v("category", x)}
            options={["Individual", "Familiar", "Empresarial"]}
          />
          <Select
            label="Status"
            value={f.status}
            set={(x) => v("status", x)}
            options={["Ativo", "Inativo", "Cancelado"]}
          />
          <F
            label="Data de adesão"
            type="date"
            value={f.joined_at}
            set={(x) => v("joined_at", x)}
          />
          <F
            label="Próximo vencimento"
            type="date"
            value={f.due_date}
            set={(x) => v("due_date", x)}
          />
          <F
            wide
            label="Responsável financeiro"
            value={f.financial_responsible}
            set={(x) => v("financial_responsible", x)}
          />
          <label className="field wide">
            <span>Observações</span>
            <textarea
              value={f.notes}
              onChange={(e) => v("notes", e.target.value)}
            />
          </label>
        </div>
      </div>
    </Modal>
  );
}
function PaymentModal({
  members,
  selected,
  close,
  save,
}: {
  members: Row[];
  selected: Row | null;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>({
    member_id: selected?.id || members[0]?.id,
    amount: 120,
    paid_at: "2026-07-20",
    frequency: "Mensal",
    method: "PIX",
  });
  return (
    <Modal title="Registrar novo pagamento" close={close} save={() => save(f)}>
      <div className="form-grid">
        <Select
          label="Membro"
          value={String(f.member_id)}
          set={(x) => setF({ ...f, member_id: Number(x) })}
          options={members.map((m) => ({ v: String(m.id), l: m.name }))}
        />
        <F
          label="Valor"
          type="number"
          value={f.amount}
          set={(x) => setF({ ...f, amount: x })}
        />
        <F
          label="Data do pagamento"
          type="date"
          value={f.paid_at}
          set={(x) => setF({ ...f, paid_at: x })}
        />
        <Select
          label="Plano"
          value={f.frequency}
          set={(x) => setF({ ...f, frequency: x })}
          options={["Mensal", "Trimestral", "Semestral", "Anual"]}
        />
        <Select
          label="Forma"
          value={f.method}
          set={(x) => setF({ ...f, method: x })}
          options={["PIX", "Dinheiro", "Cartão", "Transferência"]}
        />
      </div>
      <div className="info">
        ⓘ O próximo vencimento será calculado automaticamente ao salvar.
      </div>
    </Modal>
  );
}
function ContactModal({
  members,
  selected,
  close,
  save,
}: {
  members: Row[];
  selected: Row | null;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>({
    member_id: selected?.id || members[0]?.id,
    contacted_at: "2026-07-20",
    type: "WhatsApp",
    responsible: "Gleiton Costa",
    answered: false,
    notes: "",
    next_contact: "2026-07-27",
  });
  return (
    <Modal title="Registrar comunicação" close={close} save={() => save(f)}>
      <div className="form-grid">
        <Select
          label="Membro"
          value={String(f.member_id)}
          set={(x) => setF({ ...f, member_id: Number(x) })}
          options={members.map((m) => ({ v: String(m.id), l: m.name }))}
        />
        <F
          label="Data"
          type="date"
          value={f.contacted_at}
          set={(x) => setF({ ...f, contacted_at: x })}
        />
        <Select
          label="Tipo"
          value={f.type}
          set={(x) => setF({ ...f, type: x })}
          options={["WhatsApp", "Ligação", "E-mail"]}
        />
        <F
          label="Responsável"
          value={f.responsible}
          set={(x) => setF({ ...f, responsible: x })}
        />
        <F
          label="Próximo contato"
          type="date"
          value={f.next_contact}
          set={(x) => setF({ ...f, next_contact: x })}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={f.answered}
            onChange={(e) => setF({ ...f, answered: e.target.checked })}
          />{" "}
          Membro respondeu?
        </label>
        <label className="field wide">
          <span>Observações</span>
          <textarea
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
          />
        </label>
      </div>
    </Modal>
  );
}
function PartnerModal({
  row,
  close,
  save,
}: {
  row: Row | null;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>(
    row || {
      name: "",
      logo: "",
      category: "Saúde",
      phone: "",
      address: "Belo Horizonte/MG",
      benefit: "15%",
      status: "Ativo",
    },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function upload(file?: File) {
    if (!file) return;
    setBusy(true);
    const body = new FormData();
    body.append("action", "partner.upload");
    body.append("file", file);
    const response = await fetch("/api/data.php", { method: "POST", body });
    const json = await apiJson(response);
    if (response.ok) setF({ ...f, logo: json.url });
    else alert(json.error || "Não foi possível enviar a imagem.");
    setBusy(false);
  }
  return (
    <Modal
      title={row ? "Editar parceiro" : "Cadastrar parceiro"}
      close={close}
      save={async () => {
        if (busy) return;
        if (!f.name) return alert("Informe o nome do parceiro.");
        setBusy(true);
        setError("");
        try {
          await save({ ...f, name: String(f.name).trim() });
        } catch (reason) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Não foi possível salvar o parceiro.",
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="partner-edit-layout">
        <div className="partner-logo-editor">
          {f.logo ? (
            <img src={f.logo} alt="Logo do parceiro" />
          ) : (
            <div>Logo</div>
          )}
          <label className="outline">
            {busy ? "Enviando..." : "Alterar foto/logo"}
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
          <small>
            A imagem atual será preservada até uma nova ser escolhida.
          </small>
          {error && <div className="form-error">{error}</div>}
        </div>
        <div className="form-grid">
          <F
            label="Nome *"
            value={f.name}
            set={(x) => setF({ ...f, name: x })}
          />
          <F
            label="Categoria"
            value={f.category}
            set={(x) => setF({ ...f, category: x })}
          />
          <F
            label="Telefone"
            value={f.phone}
            set={(x) => setF({ ...f, phone: x })}
          />
          <F
            label="Benefício"
            value={f.benefit}
            set={(x) => setF({ ...f, benefit: x })}
          />
          <F
            wide
            label="Endereço"
            value={f.address}
            set={(x) => setF({ ...f, address: x })}
          />
          <Select
            label="Status"
            value={f.status || "Ativo"}
            set={(x) => setF({ ...f, status: x })}
            options={["Ativo", "Inativo"]}
          />
        </div>
      </div>
    </Modal>
  );
}
function UseModal({
  d,
  close,
  save,
}: {
  d: Data;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>({
    member_id: d.members[0]?.id,
    partner_id: d.partners[0]?.id,
    used_at: "2026-07-20",
    detail: "Desconto validado por QR Code",
  });
  return (
    <Modal title="Registrar utilização" close={close} save={() => save(f)}>
      <div className="form-grid">
        <Select
          label="Membro"
          value={String(f.member_id)}
          set={(x) => setF({ ...f, member_id: Number(x) })}
          options={d.members.map((m) => ({ v: String(m.id), l: m.name }))}
        />
        <Select
          label="Parceiro"
          value={String(f.partner_id)}
          set={(x) => setF({ ...f, partner_id: Number(x) })}
          options={d.partners.map((p) => ({ v: String(p.id), l: p.name }))}
        />
        <F
          label="Data"
          type="date"
          value={f.used_at}
          set={(x) => setF({ ...f, used_at: x })}
        />
        <F
          label="Detalhes"
          value={f.detail}
          set={(x) => setF({ ...f, detail: x })}
        />
      </div>
    </Modal>
  );
}
function F({
  label,
  value,
  set,
  type = "text",
  wide = false,
}: {
  label: string;
  value: any;
  set: (x: string) => void;
  type?: string;
  wide?: boolean;
}) {
  return (
    <label className={wide ? "field wide" : "field"}>
      <span>{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      />
    </label>
  );
}
function Select({
  label,
  value,
  set,
  options,
}: {
  label: string;
  value: string;
  set: (x: string) => void;
  options: any[];
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(e) => set(e.target.value)}>
        {options.map((o: any) => (
          <option
            key={typeof o === "string" ? o : o.v}
            value={typeof o === "string" ? o : o.v}
          >
            {typeof o === "string" ? o : o.l}
          </option>
        ))}
      </select>
    </label>
  );
}
